import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { History, Building2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_META = {
  request_created: { label: 'Request Created', cls: 'bg-green-500/15 text-green-300 border-green-500/40' },
  days_added: { label: 'Days Added', cls: 'bg-green-500/15 text-green-300 border-green-500/40' },
  request_updated: { label: 'Request Updated', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/40' },
  day_added: { label: 'Day Added', cls: 'bg-green-500/15 text-green-300 border-green-500/40' },
  day_updated: { label: 'Day Updated', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/40' },
  day_removed: { label: 'Day Removed', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40' },
  request_cancelled: { label: 'Request Cancelled', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40' },
  request_deleted: { label: 'Request Deleted', cls: 'bg-red-500/15 text-red-300 border-red-500/40' },
  job_claimed: { label: 'Job Claimed', cls: 'bg-purple-500/15 text-purple-300 border-purple-500/40' },
  staff_removed: { label: 'Staff Removed', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40' },
  document_uploaded: { label: 'Document Uploaded', cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/40' },
  document_deleted: { label: 'Document Deleted', cls: 'bg-red-500/15 text-red-300 border-red-500/40' },
  document_visibility_changed: { label: 'Doc Visibility', cls: 'bg-cyan-500/15 text-cyan-300 border-cyan-500/40' },
  message_sent: { label: 'Message Sent', cls: 'bg-zinc-500/15 text-zinc-300 border-zinc-500/40' },
};

const ROLE_CLS = {
  client: 'bg-purple-500/15 text-purple-300 border-purple-500/40',
  admin: 'bg-primary/15 text-primary border-primary/40',
  supervisor: 'bg-blue-500/15 text-blue-300 border-blue-500/40',
  employee: 'bg-green-500/15 text-green-300 border-green-500/40',
};

export default function AdminClientAuditPage() {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [filterClient, setFilterClient] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterClient !== 'all') params.set('client_id', filterClient);
      if (filterAction !== 'all') params.set('action', filterAction);
      const [aRes, cRes] = await Promise.all([
        axios.get(`${API}/admin/client-portal-audit?${params.toString()}`),
        clients.length ? Promise.resolve({ data: clients }) : axios.get(`${API}/admin/clients`).catch(() => ({ data: [] })),
      ]);
      setRows(aRes.data || []);
      if (!clients.length) setClients(cRes.data || []);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterClient, filterAction]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 space-y-4 max-w-5xl mx-auto">
        <div className="border-b border-border pb-4 flex items-center gap-3">
          <History className="w-6 h-6 text-primary" strokeWidth={1.5} />
          <div>
            <h1 className="text-2xl font-barlow tracking-widest text-foreground">CLIENT PORTAL AUDIT</h1>
            <p className="text-xs text-muted-foreground mt-1">Every action in the Client Portal — requests, jobs, documents, messages.</p>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Client</Label>
            <Select value={filterClient} onValueChange={setFilterClient}>
              <SelectTrigger className="rounded-sm h-9 text-sm w-56 bg-background border-border" data-testid="audit-filter-client">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">All clients</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.company_name || c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Action</Label>
            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger className="rounded-sm h-9 text-sm w-56 bg-background border-border" data-testid="audit-filter-action">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="all">All actions</SelectItem>
                {Object.entries(ACTION_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground ml-auto" data-testid="audit-count">{rows.length} entries</p>
        </div>

        {loading ? (
          <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground" data-testid="audit-empty">No audit entries yet.</p>
        ) : (
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-0 divide-y divide-border">
              {rows.map((r) => {
                const meta = ACTION_META[r.action] || { label: r.action, cls: 'bg-muted text-muted-foreground border-border' };
                return (
                  <div key={r.id} className="p-3.5 flex flex-col sm:flex-row sm:items-start gap-2" data-testid={`audit-row-${r.id}`}>
                    <div className="shrink-0 sm:w-40">
                      <p className="text-xs font-mono text-muted-foreground">
                        {(() => { try { return format(parseISO(r.timestamp), 'dd MMM yyyy HH:mm:ss'); } catch { return r.timestamp; } })()}
                      </p>
                      <Badge className={`text-[9px] uppercase tracking-wider border mt-1 ${meta.cls}`}>{meta.label}</Badge>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-foreground">
                        <span className="font-medium">{r.actor_name}</span>
                        <Badge variant="outline" className={`text-[9px] uppercase ml-1.5 border ${ROLE_CLS[r.actor_role] || 'border-border'}`}>
                          {r.actor_role}
                        </Badge>
                      </p>
                      {r.details && <p className="text-xs text-muted-foreground mt-0.5 break-words">{r.details}</p>}
                      {r.company_name && (
                        <p className="text-[11px] text-muted-foreground/80 mt-0.5 flex items-center gap-1">
                          <Building2 className="w-3 h-3" />{r.company_name}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
